import { FC, ChangeEvent } from 'react'
import { object, string } from 'yup'
import {
  DefaultFormContainer,
  FormItemTitle,
  FormContentContainer,
  AddressOptions,
  FormInputErrorMessage
} from './styles'
import StandardInput from '../../basic/StandardInput'
import StandardFormatInput from '../../basic/StandardFormatInput'
import StandardCheckbox from '../../basic/StandardCheckbox'
import { LeftBar } from '../../basic/LeftBar'
import OptionButton from '../../basic/OptionButton'
import { useErrors } from 'src/context/errors'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'

interface FirmSecondaryContactProps {
  setContactData: (
    { name, phoneNumber, emailAddress, isFirmAdministrator, firstName, lastName }: { [prop: string]: string | boolean | undefined }
    ) => void;
  contactData?: {
    name?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    emailAddress?: string;
    isFirmAdministrator?: boolean;
  };
  setHasSecondaryContact: (hasContact: boolean) => void;
  hasContact?: boolean;
  isIncorrect?: boolean;
  errors?: any[];
}

const FirmSecondaryContact: FC<FirmSecondaryContactProps> = ({
  setContactData,
  setHasSecondaryContact,
  hasContact,
  contactData,
  isIncorrect,
}) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setContactData({[name]: value})
  }

  const handleOptionSelect = (option: boolean) => {
    setHasSecondaryContact(option)
    if(!option) {
      setContactData({
        name: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        emailAddress: '',
        isFirmAdministrator: undefined
      })
   }
  }

  const emailValidationSchema = object({
    emailAddress: string().required('This is a required field').email('This is not a valid email')
  })

  const validateEmail = () => {
    if(!isIncorrect) {
      return ''
    }
    try {
      emailValidationSchema.validateSync({
        emailAddress: contactData?.emailAddress,
      });
      return '';
    } catch (err: any) {
      return err.message as string
    }
  }

  return (
    <DefaultFormContainer>
      <FormItemTitle>Do you want to add a secondary contact for questions about this application?</FormItemTitle>
      <AddressOptions>
        <OptionButton errorCode='EC50' isIncorrect={isIncorrect && hasContact === undefined} checked={hasContact === true} onChange={() => handleOptionSelect(true)} >Yes</OptionButton>
        <OptionButton errorCode='EC50' isIncorrect={isIncorrect && hasContact === undefined} checked={hasContact === false} onChange={() => handleOptionSelect(false)} >No</OptionButton>
      </AddressOptions>
      {(isIncorrect === true && hasContact === undefined) ? (
        <FormInputErrorMessage>
          This is a required field
        </FormInputErrorMessage>
      ) : null}
      {hasContact === true ? (
        <FormContentContainer>
          <LeftBar />
          <div className='content' style={{ maxWidth: '320px' }}>
            <StandardInput
              label="First Name"
              name="firstName"
              onChange={handleChange}
              value={contactData?.firstName ?? ''}
              isIncorrect={isIncorrect && !contactData?.firstName}
              errorMessage={(isIncorrect && !contactData?.firstName) ? 'This is a required field' : ''}
              errorCode='EC13'
            />
            <StandardInput
              label="Last Name"
              name="lastName"
              onChange={handleChange}
              value={contactData?.lastName ?? ''}
              isIncorrect={isIncorrect && !contactData?.lastName}
              errorMessage={(isIncorrect && !contactData?.lastName) ? 'This is a required field' : ''}
              errorCode='EC14'
            />
            <StandardFormatInput
              label="Phone Number"
              maskStyle='phone'
              onValueChange={({ formattedValue }) => setContactData({ phoneNumber: formattedValue })}
              value={contactData?.phoneNumber ?? ''}
              isIncorrect={isIncorrect && !contactData?.phoneNumber}
              errorMessage={(isIncorrect && !contactData?.phoneNumber) ? 'This is a required field' : ''}
              errorCode='EC15'
            />
            <StandardInput
              label="Email"
              name="emailAddress"
              onChange={handleChange}
              value={contactData?.emailAddress ?? ''}
              isIncorrect={!!validateEmail()}
              errorMessage={validateEmail()}
              errorCode='EC12'
            />
            <StandardCheckbox
              label='This person is the Firm Administrator'
              checked={contactData?.isFirmAdministrator ?? false}
              onChange={() => setContactData({ isFirmAdministrator: !contactData?.isFirmAdministrator })}
              variation='small'
            />
          </div>
        </FormContentContainer>
      ) : null}
      <ValidationErrorMessage errorCode='EC50' />

    </DefaultFormContainer>
  )
}

export default FirmSecondaryContact